var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Stack, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlanTypeCategory } from '../../../enum';
import { Autocomplete, LineBarChart, ScenarioLegendItem } from '../../common';
import { DISABLED_LEGEND_COLOR, StyledChartContainer, StyledHeaderRow, StyledLegendContainer, StyledTitleChart } from '../../common/chart/styles';
import { useReportAPI } from '../../report/selectors';
import { PLAN_COLORS } from '../../wizard/constants';
import { shouldHideProductiveHoursSection } from '../prodHours';
import { usePeriodReportByMode } from '../selectors';
import { FINANCIAL_PERIOD_CHART_METRIC_VALUES, PROD_HOURS_RESOURCING_PERIOD_CHART_METRIC_VALUES, RESOURCING_PERIOD_CHART_METRICS, RESOURCING_PERIOD_CHART_METRIC_VALUES } from './constants';
import { useChartDataset, useChartMetricOptions, useChartProperties } from './selectors';
var MetricSelector = function (_a) {
  var value = _a.value,
    options = _a.options,
    onChange = _a.onChange;
  return _jsx(Autocomplete, {
    disablePortal: true,
    sx: {
      minWidth: '14rem'
    },
    options: options,
    value: value,
    getOptionValue: function (o) {
      return o.id;
    },
    onChange: onChange
  });
};
var ScenarioLegend = function (_a) {
  var scenarios = _a.scenarios,
    visibility = _a.visibility,
    onToggle = _a.onToggle;
  var theme = useTheme();
  return _jsx(StyledLegendContainer, {
    children: scenarios.map(function (_a, index) {
      var _b;
      var scenarioName = _a.scenarioName,
        colorIndex = _a.colorIndex,
        isMultiskill = _a.isMultiskill;
      var color = ((_b = PLAN_COLORS[colorIndex]) === null || _b === void 0 ? void 0 : _b[0]) || '#1976d2';
      var isChecked = visibility[scenarioName] !== false;
      var lineColor = isChecked ? color : DISABLED_LEGEND_COLOR;
      return _jsx(ScenarioLegendItem, {
        scenarioName: scenarioName,
        isMultiskill: !!isMultiskill,
        isChecked: isChecked,
        color: color,
        lineColor: lineColor,
        label: scenarioName,
        textColor: isChecked ? color : theme.palette.text.secondary,
        onToggle: onToggle,
        theme: theme
      }, "".concat(scenarioName, "-").concat(index));
    })
  });
};
var PeriodSummaryChart = function (_a) {
  var metrics = _a.metrics;
  var t = useTranslation('report', {
    keyPrefix: 'period.chart'
  }).t;
  var metricOptions = useChartMetricOptions(metrics);
  var _b = useState(metrics[0]),
    selectedMetricKey = _b[0],
    setSelectedMetricKey = _b[1];
  var selectedOption = metricOptions.find(function (o) {
    return o.id === selectedMetricKey;
  });
  var dataset = useChartDataset(selectedMetricKey);
  var _c = useState({}),
    scenarioVisibility = _c[0],
    setScenarioVisibility = _c[1];
  var chartProperties = useChartProperties(selectedMetricKey, scenarioVisibility);
  useEffect(function () {
    if (!metrics.includes(selectedMetricKey)) {
      setSelectedMetricKey(metrics[0]);
    }
  }, [metrics, selectedMetricKey]);
  var handleToggleScenario = useCallback(function (name) {
    setScenarioVisibility(function (prev) {
      var _a;
      return __assign(__assign({}, prev), (_a = {}, _a[name] = prev[name] === undefined ? false : !prev[name], _a));
    });
  }, []);
  if (!dataset) return null;
  return _jsxs(StyledChartContainer, {
    children: [_jsxs(StyledHeaderRow, {
      children: [_jsx(StyledTitleChart, {
        children: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.name
      }), _jsxs(Stack, __assign({
        direction: "row",
        alignItems: "center",
        spacing: 2
      }, {
        children: [_jsx(Typography, __assign({
          sx: {
            whiteSpace: 'nowrap'
          }
        }, {
          children: t('selectMetric')
        })), _jsx(MetricSelector, {
          value: selectedMetricKey,
          options: metricOptions,
          onChange: setSelectedMetricKey
        })]
      }))]
    }), chartProperties && _jsx(LineBarChart, __assign({}, chartProperties)), _jsx(ScenarioLegend, {
      scenarios: dataset.scenarios,
      visibility: scenarioVisibility,
      onToggle: handleToggleScenario
    })]
  });
};
export var ResourcingPeriodChart = function () {
  var reportData = useReportAPI()[0].data;
  var periodData = usePeriodReportByMode();
  var metrics = useMemo(function () {
    if ((reportData === null || reportData === void 0 ? void 0 : reportData.planTypeCategory) !== PlanTypeCategory.ProdHours) return RESOURCING_PERIOD_CHART_METRIC_VALUES;
    var baseMetrics = __spreadArray([], PROD_HOURS_RESOURCING_PERIOD_CHART_METRIC_VALUES, true);
    if ((periodData === null || periodData === void 0 ? void 0 : periodData.length) && periodData.every(function (s) {
      return shouldHideProductiveHoursSection(s);
    })) {
      return baseMetrics.filter(function (m) {
        return m !== RESOURCING_PERIOD_CHART_METRICS.ProductiveHours;
      });
    }
    return baseMetrics;
  }, [reportData === null || reportData === void 0 ? void 0 : reportData.planTypeCategory, periodData]);
  return _jsx(PeriodSummaryChart, {
    metrics: metrics
  });
};
export var FinancialPeriodChart = function () {
  return _jsx(PeriodSummaryChart, {
    metrics: FINANCIAL_PERIOD_CHART_METRIC_VALUES
  });
};